import type { ApolloError } from '@apollo/client';
import type { useRouter } from 'next/navigation';

export const GQL_ERROR_CODES = {
  PAGE_EXPIRED: 'PAGE_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  // Add other error codes as needed
} as const;

export type GQLErrorCode = keyof typeof GQL_ERROR_CODES;

interface ErrorHandlerConfig {
  router: ReturnType<typeof useRouter>;
  trackerId: string;
  isSandbox?: boolean;
}

export const handleGQLError = (
  error: ApolloError,
  config: ErrorHandlerConfig
) => {
  const errorCode = error.graphQLErrors?.[0]?.extensions?.code as GQLErrorCode;

  switch (errorCode) {
    case GQL_ERROR_CODES.PAGE_EXPIRED: {
      const basePath = config.isSandbox ? '/sandbox' : '';
      config.router.push(`${basePath}/${config.trackerId}/expired`);
      return null;
    }

    case GQL_ERROR_CODES.UNAUTHORIZED:
      // Handle unauthorized access
      return {
        title: 'Unauthorized Access',
        message: 'You do not have permission to access this page.',
      };

    case GQL_ERROR_CODES.NOT_FOUND:
      // Handle not found
      return {
        title: 'Not Found',
        message: 'The requested resource could not be found.',
      };

    case GQL_ERROR_CODES.VALIDATION_ERROR:
      // Handle validation errors
      return {
        title: 'Validation Error',
        message: error.message || 'Please check your input and try again.',
      };

    default:
      // Handle unknown errors
      return {
        title: 'Error',
        message: error.message || 'An unexpected error occurred.',
      };
  }
};
